import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import styled from 'styled-components';

const LegalNoticeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: 5rem;
  
  @media (min-width: 1024px) {
    padding-top: 8rem;
  }
`;

const SectionTitle = styled.h1`
  color: var(--primary-color);
  margin-bottom: 2rem;
  text-align: center;
`;

const ContentSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionHeading = styled.h3`
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
`;

const TextContent = styled.p`
  margin-bottom: 0.5rem;
  line-height: 1.6;
`;

const LegalNoticePage: React.FC = () => {
  const { language, translations } = useLanguage();

  useEffect(() => {
    // Update page title based on language
    let title = 'Impressum';
    if (language === 'en') title = 'Legal Notice';
    if (language === 'es') title = 'Aviso Legal';
    if (language === 'ru') title = 'Юридическая информация';
    document.title = `SeaDent Mallorca - ${title}`;
  }, [language]);

  // Render content based on the current language
  const renderContent = () => {
    switch (language) {
      case 'de':
        return (
          <>
            <ContentSection>
              <SectionHeading>{translations.legalNotice.impressumTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.german.tmgInfo}</TextContent>
              <TextContent>{translations.legalNotice.practiceName}</TextContent>
              <TextContent>{translations.legalNotice.address}</TextContent>
              <TextContent>{translations.legalNotice.cityZip}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.representedBy}</SectionHeading>
              <TextContent>{translations.legalNotice.doctorName}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.contactTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.german.phone}</TextContent>
              <TextContent>{translations.legalNotice.email}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.hoursTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.german.regularHours}</TextContent>
              <TextContent>{translations.legalNotice.german.emergencyHours}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.professionalTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.german.designation}</TextContent>
              <TextContent>{translations.legalNotice.german.conferredIn}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.german.contentResponsibility}</SectionHeading>
              <TextContent>{translations.legalNotice.doctorName}</TextContent>
              <TextContent>{translations.legalNotice.address}</TextContent>
              <TextContent>{translations.legalNotice.cityZip}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.disclaimerTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.german.disclaimer}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.copyrightTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.german.copyright}</TextContent>
            </ContentSection>

            <TextContent>{translations.legalNotice.german.date}</TextContent>
          </>
        );

      case 'en':
        return (
          <>
            <ContentSection>
              <SectionHeading>{translations.legalNotice.legalNoticeTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.english.tmgInfo}</TextContent>
              <TextContent>{translations.legalNotice.practiceName}</TextContent>
              <TextContent>{translations.legalNotice.address}</TextContent>
              <TextContent>{translations.legalNotice.cityZipEn}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.representedBy}</SectionHeading>
              <TextContent>{translations.legalNotice.doctorName}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.contactTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.english.phone}</TextContent>
              <TextContent>{translations.legalNotice.email}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.hoursTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.english.regularHours}</TextContent>
              <TextContent>{translations.legalNotice.english.emergencyHours}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.professionalTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.english.designation}</TextContent>
              <TextContent>{translations.legalNotice.english.conferredIn}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.english.contentResponsibility}</SectionHeading>
              <TextContent>{translations.legalNotice.doctorName}</TextContent>
              <TextContent>{translations.legalNotice.address}</TextContent>
              <TextContent>{translations.legalNotice.cityZipEn}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.disclaimerTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.english.disclaimer}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.copyrightTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.english.copyright}</TextContent>
            </ContentSection>

            <TextContent>{translations.legalNotice.english.date}</TextContent>
          </>
        );

      case 'ru':
        return (
          <>
            <ContentSection>
              <SectionHeading>{translations.legal.title}</SectionHeading>
              <TextContent>{translations.legal.responsibleEntity}</TextContent>
              <TextContent>{translations.legal.companyName}</TextContent>
              <TextContent>{translations.legalNotice.address}</TextContent>
              <TextContent>07012 Пальма-де-Майорка, Испания</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legal.contactInfo}</SectionHeading>
              <TextContent>{translations.legalNotice.doctorName}</TextContent>
              <TextContent>Телефон: 603 31 96 48</TextContent>
              <TextContent>Эл. почта: info@seadent-mallorca.com</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.footer.hoursTitle}</SectionHeading>
              <TextContent>{translations.footer.mondayToThursday}</TextContent>
              <TextContent>{translations.footer.friday}</TextContent>
              <TextContent>Экстренная помощь: 24/7 по телефону 603 31 96 48</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legal.registrationInfo}</SectionHeading>
              <TextContent>Стоматолог, профессиональная лицензия выдана в Испании</TextContent>
              <TextContent>{translations.legal.vatId}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legal.disclaimer}</SectionHeading>
              <TextContent>{translations.legal.disclaimerText}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legal.copyright}</SectionHeading>
              <TextContent>{translations.legal.copyrightText}</TextContent>
            </ContentSection>

            <TextContent>Последнее обновление: Июнь 2023</TextContent>
          </>
        );

      case 'es':
      default:
        return (
          <>
            <ContentSection>
              <SectionHeading>{translations.legalNotice.avisoLegalTitle}</SectionHeading>
              <TextContent>{translations.legalNotice.spanish.tmgInfo}</TextContent>
              <TextContent>{translations.legalNotice.practiceName}</TextContent>
              <TextContent>{translations.legalNotice.address}</TextContent>
              <TextContent>{translations.legalNotice.cityZipEs}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.representedByEs}</SectionHeading>
              <TextContent>{translations.legalNotice.doctorName}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.contactTitleEs}</SectionHeading>
              <TextContent>{translations.legalNotice.spanish.phone}</TextContent>
              <TextContent>{translations.legalNotice.emailEs}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.hoursTitleEs}</SectionHeading>
              <TextContent>{translations.legalNotice.spanish.regularHours}</TextContent>
              <TextContent>{translations.legalNotice.spanish.emergencyHours}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.professionalTitleEs}</SectionHeading>
              <TextContent>{translations.legalNotice.spanish.designation}</TextContent>
              <TextContent>{translations.legalNotice.spanish.conferredIn}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.spanish.contentResponsibility}</SectionHeading>
              <TextContent>{translations.legalNotice.doctorName}</TextContent>
              <TextContent>{translations.legalNotice.address}</TextContent>
              <TextContent>{translations.legalNotice.cityZipEs}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.disclaimerTitleEs}</SectionHeading>
              <TextContent>{translations.legalNotice.spanish.disclaimer}</TextContent>
            </ContentSection>

            <ContentSection>
              <SectionHeading>{translations.legalNotice.copyrightTitleEs}</SectionHeading>
              <TextContent>{translations.legalNotice.spanish.copyright}</TextContent>
            </ContentSection>

            <TextContent>{translations.legalNotice.spanish.date}</TextContent>
          </>
        );
    }
  };

  return (
    <LegalNoticeContainer>
      <SectionTitle>{translations.legalNotice.title}</SectionTitle>
      {renderContent()}
    </LegalNoticeContainer>
  );
};

export default LegalNoticePage; 